import React, { useEffect, useState } from 'react';
import Styled from 'styled-components';
import { format } from 'date-fns';
import { Flex } from '@components/layout/Grid';
import { Modal, Header } from '@organisms/modals/base';
import PaymentConfirmationMessage from './SubscriptionPaymentConfirmation';
import { GenericPaymentFailedMessage } from './GenericPaymentFailedMessage';
import { ProgramPaymentConfirmationMessage } from './ProgramPaymentConfirmationMessage';
import { SubscriptionPaymentFailedMessage } from './SubscriptionPaymentFailedMessage';
import Spinner from '@components/Spinner';
import { hideURLParams } from '@lib/helpers';
import AnalyticsManager from '@lib/analytics/manager';
import { PAYMENT_CONFIRMATION_ACTION } from '@lib/constants';
import { OrderStatus, useOrderQuery } from '@gql/generated';
import { VoucherPaymentConfirmationMessage } from './VoucherPaymentConfirmationModal';
import PaymentPendingMessage from './SubscriptionPaymentPending';

const StyledModal = Styled(Modal)`
  overflow-y: scroll;

  .MuiBackdrop-root {
    display: none;
  }

  .MuiDialog-container {
    position: relative;
    height: unset;
    min-height: 100vh;
  }

  .MuiDialog-paperScrollPaper {
    max-height: none;
  }

  .MuiDialog-container.MuiDialog-scrollPaper:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    /* bottom: 0; */
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

declare type Props = {
  open?: boolean;
  orderId: string;
  onClose?(): void;
};

const PaymentConfirmationModal = (props: Props) => {
  const { open = false, orderId } = props;
  const [isOpen, setIsOpen] = useState(open);
  const { data, loading } = useOrderQuery({
    variables: {
      id: orderId,
    },
  });

  useEffect(() => {
    const order = data?.order;
    //
    if (order?.__typename === 'SubscriptionPayment') {
      if (
        order?.status === OrderStatus['Completed'] ||
        order?.status === OrderStatus['Pending']
      ) {
        AnalyticsManager().sendSubscriptionPurchaseCompleteEvent({
          orderId: order.orderId,
          months: order.months,
          value: Math.floor(order?.amountCents / 100),
          original_amount: Math.floor(order?.originalAmountCents / 100),
          currency: order?.amountCurrency,
          payment_type: order?.paymentProvider,
        });
      } else {
        AnalyticsManager().SendSubscriptionPurchaseFailEvent({
          orderId: order.orderId,
          months: order.months,
          value: Math.floor(order?.amountCents / 100),
          original_amount: Math.floor(order?.originalAmountCents / 100),
          currency: order?.amountCurrency,
          payment_type: order?.paymentProvider,
        });
      }
    } else if (order?.__typename === 'GenericProgramPayment') {
      if (order?.status === OrderStatus['Completed']) {
        AnalyticsManager().sendProgramAccessGrantedEvent({
          id: order?.program?.id,
          title: order?.program?.title,
          slug: order?.program?.slug,
          category: order?.program?.category,
        });
        AnalyticsManager().sendCoursePaymentCompleteEvent({
          order_id: orderId,
          item_type: order?.program?.category,
          id: order?.program?.id,
          title: order?.program?.title,
          slug: order?.program?.slug,
          transaction_id: orderId,
          value: Math.floor(order?.amountCents / 100),
          original_amount: Math.floor(order?.originalAmountCents / 100),
          currency: order?.amountCurrency,
          payment_type: order?.paymentProvider,
          voucher_purchase: order?.voucher,
        });
      } else {
        AnalyticsManager().sendCoursePaymentFailedEvent({
          order_id: orderId,
          item_type: order?.program?.category,
          id: order?.program?.id,
          title: order?.program?.title,
          slug: order?.program?.slug,
          value: Math.floor(order?.amountCents / 100),
          original_amount: Math.floor(order?.originalAmountCents / 100),
          currency: order?.amountCurrency,
          payment_type: order?.paymentProvider,
          voucher_purchase: order?.voucher,
        });
      }
    }
  }, [data?.order?.orderId]);

  const onClose = () => {
    setIsOpen(false);
    hideURLParams(PAYMENT_CONFIRMATION_ACTION);
    props?.onClose?.();
  };

  let content = null;
  const order = data?.order;
  if (order?.__typename === 'SubscriptionPayment') {
    if (order?.status === OrderStatus['Pending']) {
      //
      content = <PaymentPendingMessage onClose={onClose} />;
    } else if (order?.status === OrderStatus['Completed']) {
      content = (
        <PaymentConfirmationMessage
          currency={order?.amountCurrency}
          months={order?.months}
          paymentAmount={Math.floor(order?.amountCents / 100)}
          recurringPaymentAmount={Math.floor(order?.recurringAmountCents / 100)}
          recurringDate={format(new Date(order?.recurringDate), 'MMM dd yyyy')}
          onClose={onClose}
        />
      );
    } else if (order?.status === OrderStatus['Failed']) {
      content = <SubscriptionPaymentFailedMessage />;
    }
  } else if (order?.__typename === 'GenericProgramPayment') {
    if (order?.status === OrderStatus['Completed']) {
      if (order?.voucher) {
        content = (
          <VoucherPaymentConfirmationMessage
            onClose={onClose}
            amountCents={order?.amountCents}
            currency={order.amountCurrency}
            programCategory={order?.program.category}
          />
        );
      } else {
        content = (
          <ProgramPaymentConfirmationMessage
            amountCents={order?.amountCents}
            currency={order?.amountCurrency}
            onClose={onClose}
          />
        );
      }
    } else {
      content = <GenericPaymentFailedMessage onClose={onClose} />;
    }
  }
  // LivePayment is not covered here because live payments have a confirmation page
  // AddPayment only shows a snackBar so not modal here
  else {
    return null;
  }

  return isOpen ? (
    <StyledModal open={isOpen} handleClose={onClose}>
      <Header handleClose={onClose} />
      {loading ? (
        <Flex aligContent="center">
          <Spinner size={24} />
        </Flex>
      ) : null}
      {content}
    </StyledModal>
  ) : null;
};

export default PaymentConfirmationModal;
